/**
 * Shared helpers for the tenant detail tab components (Phase 5 Action 49).
 *
 * Kept in one place so a formatting tweak (date locale, relative-time
 * thresholds, empty-state em-dash) lands consistently across every tab.
 */

import { apiPath } from '../../api/baseUrl';
import { getStoredToken } from '../../auth/AuthContext';

export async function fetchJson<T>(url: string): Promise<T> {
  const token = getStoredToken();
  const res = await fetch(apiPath(url), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`${url} → ${res.status}`);
  return res.json();
}

export function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diffMs = Date.now() - then;
  const day = 24 * 60 * 60 * 1000;
  if (diffMs < 60 * 1000) return 'just now';
  if (diffMs < 60 * 60 * 1000) return `${Math.floor(diffMs / (60 * 1000))}m ago`;
  if (diffMs < day) return `${Math.floor(diffMs / (60 * 60 * 1000))}h ago`;
  if (diffMs < 7 * day) return `${Math.floor(diffMs / day)}d ago`;
  return formatDate(iso);
}

export interface TenantContext {
  tenant: {
    id: string;
    name: string;
    slug: string;
    status: 'provisioning' | 'active' | 'suspended' | 'trial' | 'deleted';
    primaryDomain: string | null;
    timezone: string;
    locale: string;
    activatedAt: string | null;
    createdAt: string;
  };
}
